import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };
const COLLAPSE_LIMIT = 12;

/**
 * Reusable character-selection grid — extracted from AudiobookCreatePage's
 * character step so it can be shared with other storybook creation flows
 * (e.g. AI Voice Storybook creation) without duplicating the markup.
 */
const CharacterSelectGrid = ({
  characters = [],
  selectedCharacters = [],
  onToggle,
  maxSelected = 3,
  heading = "Pick your friends",
  subheading = `Choose 1–${maxSelected} characters to star in your storybook.`,
}) => {
  const navigate = useNavigate();
  const [characterSearch, setCharacterSearch] = useState("");
  const [showAllCharacters, setShowAllCharacters] = useState(false);

  const q = characterSearch.trim().toLowerCase();
  const filteredChars = q
    ? characters.filter((c) => (c.name || "").toLowerCase().includes(q))
    : characters;
  const overLimit = filteredChars.length > COLLAPSE_LIMIT;
  const visibleChars = overLimit && !showAllCharacters
    ? filteredChars.slice(0, COLLAPSE_LIMIT)
    : filteredChars;
  const selectedIds = new Set(selectedCharacters.map((c) => c.id));

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h2 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 22, letterSpacing: "-0.01em", textTransform: "uppercase", margin: 0 }}>
          {heading}
        </h2>
        <span style={{ ...FREDOKA, color: "#0065F8", fontWeight: 800, fontSize: 13, letterSpacing: "0.04em", background: "rgba(0,101,248,0.10)", padding: "4px 10px", borderRadius: 999 }}>
          {selectedCharacters.length} / {maxSelected}
        </span>
      </div>
      <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: "0 0 16px" }}>
        {subheading}
      </p>

      {characters.length > 6 && (
        <div
          className="relative mb-4"
          style={{
            background: "#FFFFFF",
            border: "2px solid rgba(11,26,51,0.08)",
            borderRadius: 14,
            boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 14px",
          }}
        >
          <svg width="18" height="18" fill="none" stroke="#5A6B85" strokeWidth="2.4" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="7" />
            <path strokeLinecap="round" d="m20 20-3.5-3.5" />
          </svg>
          <input
            type="text"
            value={characterSearch}
            onChange={(e) => setCharacterSearch(e.target.value)}
            placeholder="Search characters by name…"
            className="flex-1 bg-transparent focus:outline-none"
            style={{ ...FREDOKA, color: "#0B1A33", fontSize: 14 }}
          />
          {characterSearch && (
            <button
              type="button"
              onClick={() => setCharacterSearch("")}
              aria-label="Clear search"
              style={{ color: "#5A6B85", padding: 2, cursor: "pointer" }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          )}
        </div>
      )}

      {characters.length === 0 ? (
        <div className="text-center py-12">
          <p style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 20, margin: "0 0 8px" }}>No characters yet</p>
          <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 14, margin: "0 0 20px" }}>You need a character before starting a storybook.</p>
          <button
            type="button"
            onClick={() => navigate("/character-lab")}
            className="inline-flex items-center gap-2 px-6 py-3 transition-all active:scale-[0.98]"
            style={{
              ...FREDOKA,
              background: "#0065F8",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: 14,
              border: "none",
              borderRadius: 999,
              boxShadow: "0px 4px 0px #073B8E",
              cursor: "pointer",
            }}
          >
            Create Your First Character
          </button>
        </div>
      ) : filteredChars.length === 0 ? (
        <div
          className="text-center py-10"
          style={{
            background: "#FFFFFF",
            border: "2px solid rgba(11,26,51,0.06)",
            borderRadius: 18,
          }}
        >
          <p style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 15, margin: "0 0 4px" }}>
            No characters match "{characterSearch}"
          </p>
          <p style={{ ...FREDOKA, color: "#5A6B85", fontSize: 13, margin: 0 }}>
            Try a different name or clear the search.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
            {visibleChars.map((character) => {
              const isSelected = selectedIds.has(character.id);
              const isDisabled = selectedCharacters.length >= maxSelected && !isSelected;
              return (
                <button
                  key={character.id}
                  type="button"
                  onClick={() => onToggle?.(character)}
                  disabled={isDisabled}
                  className="relative overflow-hidden transition-all"
                  style={{
                    background: "#FFFFFF",
                    border: isSelected ? "3px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
                    borderRadius: 18,
                    boxShadow: isSelected
                      ? "0px 4px 0px #073B8E, 0px 12px 28px rgba(0,101,248,0.18)"
                      : "0px 2px 0px rgba(11,26,51,0.04), 0px 4px 12px rgba(11,26,51,0.04)",
                    opacity: isDisabled ? 0.45 : 1,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    transform: isSelected ? "translateY(-2px)" : "none",
                  }}
                >
                  <div className="aspect-square overflow-hidden" style={{ background: "#EAF3FF" }}>
                    <img
                      src={character.animated_image_url || character.handdrawn_image_url || character.image_url || "/assets/handdrawn-character.png"}
                      alt={character.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = "/assets/handdrawn-character.png"; }}
                    />
                  </div>
                  <div className="px-2 py-2 text-center">
                    <p style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 13, margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {character.name}
                    </p>
                  </div>
                  {isSelected && (
                    <div style={{ position: "absolute", top: 8, right: 8, width: 28, height: 28, borderRadius: "50%", background: "#0065F8", boxShadow: "0px 2px 0px #073B8E", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="16" height="16" fill="none" stroke="#fff" strokeWidth="3" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {overLimit && (
            <button
              type="button"
              onClick={() => setShowAllCharacters((v) => !v)}
              className="w-full mt-4 py-2.5 transition-all"
              style={{
                ...FREDOKA,
                background: "#FFFFFF",
                color: "#0065F8",
                fontWeight: 700,
                fontSize: 13,
                border: "2px solid rgba(0,101,248,0.18)",
                borderRadius: 999,
                boxShadow: "0px 2px 0px rgba(11,26,51,0.04)",
              }}
            >
              {showAllCharacters ? "Show less" : `Show all ${filteredChars.length} characters`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default CharacterSelectGrid;
